import { type SerializeFrom } from '@remix-run/node';
import { useRouteLoaderData } from '@remix-run/react';
import { isAfter } from 'date-fns';
import { z } from 'zod';
import { type loader as rootLoader } from '#app/root.tsx';
import {
  ROLE_NON_VETERAN_MEMBER,
  ROLE_VERIFIED_VETERAN,
  SUBSCRIPTION_ACTIVE,
  SUBSCRIPTION_PENDING_CONFIRMATION,
} from './constants';
import { toDate } from './formatters';

function isUser(user: any): user is NonNullable<SerializeFrom<typeof rootLoader>['user']> {
  return Boolean(user && typeof user === 'object' && typeof user.id === 'string');
}

export type UseOptionalUser = ReturnType<typeof useOptionalUser>;

export function useOptionalUser() {
  const data = useRouteLoaderData<typeof rootLoader>('root');
  if (!data || !isUser(data.user)) {
    return undefined;
  }

  // Normalize roles and permissions (remove JsonifyObject type)
  const roles = Array.isArray(data.user.roles)
    ? data.user.roles.map((role) => ({
        name: role.name,
        permissions: Array.isArray(role.permissions)
          ? role.permissions.map((permission) => ({
              entity: permission.entity,
              action: permission.action,
              access: permission.access,
            }))
          : [],
      }))
    : [];

  // Normalize crmResponses (remove JsonifyObject type)
  const crmResponses = Array.isArray(data.user.crmResponses)
    ? data.user.crmResponses.map((response) => ({
        ...response,
        createdAt: toDate(response.createdAt),
        updatedAt: toDate(response.updatedAt),
      }))
    : [];

  // Normalize membership (remove JsonifyObject type)
  const membership = data.user.membership
    ? {
        ...data.user.membership,
        createdAt: toDate(data.user.membership.createdAt),
        verifiedAt: toDate(data.user.membership.verifiedAt),
        arrivalAt: toDate(data.user.membership.arrivalAt),
      }
    : null;

  // Normalize subscriptions (remove JsonifyObject type)
  const subscriptions = Array.isArray(data.user.subscriptions)
    ? data.user.subscriptions.map((subscription) => ({
        ...subscription,
        activeUntil: toDate(subscription.activeUntil),
      }))
    : [];

  return {
    // Normalize user (remove JsonifyObject type)
    ...data.user,
    createdAt: toDate(data.user.createdAt),
    emailVerifiedAt: toDate(data.user.emailVerifiedAt),
    verifiedAt: toDate(data.user.verifiedAt),
    activatedAt: toDate(data.user.activatedAt),

    roles,
    crmResponses,
    membership,
    subscriptions,
  };
}

export function useUser() {
  const maybeUser = useOptionalUser();
  if (!maybeUser) {
    throw new Error(
      'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.'
    );
  }
  return maybeUser;
}

// Permission string validation
const permissionRegex = /^[a-z_]+:[a-z_]+:(own|any|own,any|any,own)?$/;
export const permissionSchema = z.string().regex(permissionRegex);

export function isValidPermissionString(value: string) {
  return permissionSchema.safeParse(value).success;
}

export function parsePermissionString(permissionString: string) {
  if (!isValidPermissionString(permissionString)) {
    return {
      action: undefined,
      entity: undefined,
      access: undefined,
    };
  }
  const [action, entity, access] = permissionString.split(':');
  return {
    action,
    entity,
    access: access ? (access.split(',') as Array<'own' | 'any'>) : undefined,
  };
}

type UserHasPermissionOptions = {
  user: Pick<ReturnType<typeof useUser>, 'roles'> | null | undefined;
  permission: string;
};

/**
 * @deprecated Refer to "components/permission/README.md" for the new permission system
 */
export function userHasPermission({ user, permission }: UserHasPermissionOptions) {
  if (!user) return false;
  const { action, entity, access } = parsePermissionString(permission);
  return user.roles.some((role) =>
    role.permissions.some(
      (permission) =>
        permission.entity === entity && permission.action === action && (!access || access.includes(permission.access))
    )
  );
}

export function userHasRole(user: Pick<ReturnType<typeof useUser>, 'roles'> | null, role: string) {
  if (!user) return false;
  return user.roles.some((r) => r.name === role);
}

export function publicProfileUrl({ cardNumber, baseUrl }: { cardNumber: string | null | undefined; baseUrl: string }) {
  if (!cardNumber) {
    return null;
  }

  return `${baseUrl}/p/${cardNumber}`;
}

export function isVerified(user: { roles: { name: string }[] }) {
  return user.roles.some((role) => role.name === ROLE_VERIFIED_VETERAN);
}

export function isNonVeteran(user: { roles: { name: string }[] }) {
  return user.roles.some((role) => role.name === ROLE_NON_VETERAN_MEMBER);
}

/**
 * * This is available on `rootData.hasActiveSubscription` so you typically won't need to call this
 *
 * * const rootData = useRouteLoaderData<typeof rootLoader>('root')
 */
export function hasActiveSubscription(subscription: { status: string; activeUntil: Date } | undefined | null) {
  if (!subscription) return false;

  // True if subscription status is SUBSCRIPTION_ACTIVE, duh.
  if (subscription.status === SUBSCRIPTION_ACTIVE) return true;

  // True if activeUntil has not passed, this is for the case where the subscription status is SUBSCRIPTION_CANCELED but still has not expired yet
  if (isAfter(subscription.activeUntil, new Date())) return true;

  // If the activeUntil date has passed, return true while subscription status is SUBSCRIPTION_PENDING_CONFIRMATION
  if (subscription.status === SUBSCRIPTION_PENDING_CONFIRMATION) return true;

  return false;
}

export function defaultRedirectTo(isAdmin: boolean | null | undefined) {
  return isAdmin ? '/admin/dashboard' : '/member';
}
